import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import "tom-select/dist/css/tom-select.default.css";
import "../stylesheets/tom_select.css";

// A searchable picker on top of a plain <select>:
//
//   = f.select :person_id, options, {}, {data: {controller: "tom-select"}}
//
// The <select> stays the source of truth (it is hidden, not replaced), so
// forms submit as before and server code and specs see the same params. Tom
// Select keeps its value in sync and fires a native `change` event on it.
//
// * An empty option (`include_blank`) is the placeholder, and a chosen value
//   can be cleared with the x button. When the empty option has a text of its
//   own ("--- nic ---"), it stays in the list as a choice.
// * `multiple` selects show their choices as removable chips.
// * Tab after typing chooses the highlighted suggestion, as Enter does, and
//   moves on. With nothing typed (or the list closed) it only moves on:
//   the first option is highlighted all the time, which is no choice.
// * The option list is not capped (Tom Select shows 50 by default).
// * With `data-tom-select-codes-value="true"` the options carry a
//   `data-code` (a registration number) and typing codes separated by commas
//   ("DKP0007, DKP0008,") chooses them (what is not a code stays typed).
//
// Stimulus connects and disconnects with the element, so selects inserted by
// a Turbo Stream or frame need no extra setup.
export default class extends Controller {
  static values = {codes: Boolean};

  connect() {
    const plugins = [];
    const blank = this.element.querySelector('option[value=""]');
    // A blank option with a text ("--- nic ---") is a choice like any other.
    const blankIsChoice = !!blank && blank.textContent.trim() !== "";
    if (this.element.multiple) {
      plugins.push("remove_button");
    } else {
      // The search box in the list, as select2 had it.
      plugins.push("dropdown_input");
      if (blank && !blankIsChoice) plugins.push("clear_button");
    }

    this.picker = new TomSelect(this.element, {
      plugins,
      maxOptions: null,
      allowEmptyOption: blankIsChoice,
      // As select2 did: choosing closes the list (also for several choices).
      closeAfterSelect: true,
      selectOnTab: true,
      // Filter as you type: the options are all in the page, there is
      // nothing to wait for (the default 300ms is for remote loading).
      refreshThrottle: 0,
      render: {
        no_results: () => '<div class="no-results">Nenalezeny žádné položky.</div>',
      },
    });
    // Before Tom Select sees the Tab: with nothing typed there is no suggestion
    // to take, so close the list and let the Tab move the focus.
    this.picker.wrapper.addEventListener("keydown", this.tabWithoutText, true);
    if (this.codesValue) this.picker.on("type", (text) => this.chooseTypedCodes(text));
  }

  tabWithoutText = (event) => {
    if (event.key === "Tab" && this.picker.isOpen && this.picker.control_input.value === "") this.picker.close();
  };

  // Chooses the options whose code was typed in a comma separated list.
  chooseTypedCodes(text) {
    if (!text.includes(",")) return;
    const options = Array.from(this.element.options);
    const rest = [];
    text.split(",").forEach((part) => {
      const code = part.trim().toUpperCase();
      if (code === "") return;
      const option = options.find((o) => (o.dataset.code || "").toUpperCase() === code);
      if (option) {
        this.picker.addItem(option.value);
      } else {
        rest.push(part.trim());
      }
    });
    const remaining = rest.join(",");
    this.picker.setTextboxValue(remaining);
    this.picker.lastValue = remaining;
    this.picker.refreshOptions(false);
  }

  disconnect() {
    this.picker?.wrapper.removeEventListener("keydown", this.tabWithoutText, true);
    this.picker?.destroy();
    this.picker = null;
  }
}
